import React from 'react';
import { motion } from 'motion/react';
import CaseStudies from '@/src/components/CaseStudies';
import Blog from '@/src/components/Blog';
import { FileText, Download, Shield, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '@/src/components/SEO';

export default function Resources() {
  return (
    <div className="flex flex-col">
      <SEO 
        title="Cybersecurity Resources & Intelligence"
        description="Access AGRANDINTL's library of cybersecurity case studies, threat intelligence reports, and strategic guides for security leaders."
      />
      {/* Hero */}
      <section className="pt-40 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 left-0 w-full h-full cyber-grid opacity-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-[10px] font-bold uppercase tracking-[0.4em] mb-12 backdrop-blur-md"
            >
              <Shield className="w-3 h-3" /> Knowledge & Intelligence
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
              className="text-6xl md:text-9xl font-display font-bold mb-12 tracking-tighter leading-[0.8]"
            >
              Business <br />
              <span className="text-accent">Insights</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-2xl text-muted-foreground font-light leading-relaxed mb-16 max-w-2xl"
            >
              Access our library of technical case studies, threat intelligence reports, and strategic guides designed for business owners and security leaders.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Featured Download */}
      <section className="pb-24">
        <div className="container mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-[3rem] p-12 md:p-20 border-accent/20 bg-accent/5 relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 p-12 opacity-5 group-hover:opacity-10 group-hover:scale-110 transition-all duration-700">
              <FileText className="w-64 h-64" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
              <div className="space-y-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 border border-accent/30 text-accent text-[10px] font-bold uppercase tracking-widest">
                  Featured Intelligence
                </div>
                <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight">2026 Cyber <br />Risk Report</h2>
                <p className="text-lg text-muted-foreground font-light leading-relaxed">
                  Download our definitive guide to the evolving threat landscape. Learn how modern organizations are defending against AI-driven attacks, ransomware, and cloud misconfigurations.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg" variant="accent" className="h-14 px-8 rounded-xl font-bold flex items-center gap-3 shadow-[0_0_20px_rgba(0,150,255,0.2)]">
                    <Link to="/resources/risk-report">
                      <Download className="w-5 h-5" /> Download Free Report
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="h-14 px-8 rounded-xl border-white/10 hover:bg-white/5 font-bold">
                    <Link to="/audit">Get Custom Audit</Link>
                  </Button>
                </div>
              </div>
              <div className="hidden lg:block relative">
                <div className="aspect-[3/4] bg-background/40 rounded-2xl border border-white/10 shadow-2xl p-8 flex flex-col justify-between">
                  <div className="space-y-4">
                    <div className="w-full h-4 bg-accent/20 rounded-full" />
                    <div className="w-3/4 h-4 bg-white/5 rounded-full" />
                    <div className="w-full h-4 bg-white/5 rounded-full" />
                    <div className="w-1/2 h-4 bg-white/5 rounded-full" />
                  </div>
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center text-accent">
                        <Shield className="w-6 h-6" />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="w-full h-3 bg-white/10 rounded-full" />
                        <div className="w-1/2 h-3 bg-white/5 rounded-full" />
                      </div>
                    </div>
                    <div className="p-4 rounded-xl bg-accent/5 border border-accent/20">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-accent mb-2">Critical Vulnerability Found</p>
                      <div className="w-full h-2 bg-accent/30 rounded-full" />
                    </div>
                  </div>
                </div>
                {/* Decorative elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent/20 blur-3xl rounded-full" />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-accent/10 blur-3xl rounded-full" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog & Case Studies */}
      <Blog />
      <CaseStudies />

      {/* CTA */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="glass-card rounded-[3rem] p-16 text-center space-y-8 bg-accent/5 border-accent/20">
            <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight">Ready to Secure Your Future?</h2>
            <p className="text-lg text-muted-foreground font-light max-w-2xl mx-auto">
              Our intelligence team is available for a confidential strategy session to discuss your organization's specific security needs.
            </p>
            <Button asChild size="lg" variant="accent" className="h-16 px-12 rounded-full font-bold text-lg shadow-[0_0_30px_rgba(0,150,255,0.3)]">
              <Link to="/contact">Book Free Consultation</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
